import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Ticket, Copy, CheckCircle, WhatsappLogo, ShoppingBag, Gift, Trophy } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function MyCoupons() {
  const { user } = useAuth();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    if (!user || user === false) { setLoading(false); return; }
    (async () => {
      try { const { data } = await api.get("/coupons/mine"); setCoupons(data || []); }
      catch { toast.error("Couldn't load coupons"); }
      finally { setLoading(false); }
    })();
  }, [user]);

  const copy = async (code) => {
    try { await navigator.clipboard.writeText(code); setCopied(code); toast.success("Coupon copied"); setTimeout(() => setCopied(null), 2000); }
    catch { toast.error("Couldn't copy"); }
  };
  const shareWA = (c) => {
    const msg = `🎉 I won a $${c.amount_usd} coupon on Anveshan!\nCode: ${c.code}\nUse it at ${window.location.origin}/marketplace before ${c.expires_at ? new Date(c.expires_at).toLocaleDateString() : "it expires"}.`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  if (!user || user === false) {
    return <div className="max-w-4xl mx-auto px-6 py-16 samaaj-card p-10 text-center text-[#5C564D]">Log in to see your coupons.</div>;
  }

  const active = coupons.filter((c) => !c.redeemed && !c.disabled && (!c.expires_at || new Date(c.expires_at) > new Date()));
  const spent = coupons.filter((c) => c.redeemed);
  const expired = coupons.filter((c) => !c.redeemed && (c.disabled || (c.expires_at && new Date(c.expires_at) <= new Date())));
  const totalActive = active.reduce((s, c) => s + (c.amount_usd || 0), 0);

  return (
    <div className="max-w-4xl mx-auto px-6 py-12" data-testid="coupons-page">
      <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#D95A41]">Coupon wallet · <span style={{ color: "#8A6E24" }}>legacy</span></div>
      <h1 className="font-heading text-5xl mt-2">Your <em className="not-italic text-[#2C4A3B]">winnings</em>.</h1>
      <p className="text-[#5C564D] mt-2 max-w-2xl">Coupons you earned earlier are preserved here as personal records.</p>
      <div className="mt-4 p-4 rounded-xl border" style={{ background: "#FFF4D8", borderColor: "#E6CF91", color: "#6B5213" }} data-testid="legacy-notice">
        <div className="text-xs font-bold uppercase tracking-widest">Legacy — read-only</div>
        <p className="text-sm mt-1">Anveshan no longer processes payments. These coupons are kept as a personal record only and can’t be redeemed on the platform. Please contact the seller directly to arrange payment or Cash on Delivery.</p>
      </div>

      <div className="mt-6 grid grid-cols-3 gap-3">
        <StatCard label="Active" value={active.length} sub={totalActive ? `$${totalActive} total` : "$0"} accent />
        <StatCard label="Spent" value={spent.length} sub="Redeemed" />
        <StatCard label="Expired" value={expired.length} sub="No longer usable" />
      </div>

      {loading ? (
        <div className="mt-8 samaaj-card p-8 text-center text-[#5C564D]">Loading…</div>
      ) : active.length === 0 ? (
        <div className="mt-8 samaaj-card p-10 text-center">
          <Gift size={40} weight="duotone" className="mx-auto text-[#2C4A3B]" />
          <div className="font-heading text-2xl mt-2">No active coupons yet</div>
          <p className="text-sm text-[#5C564D] mt-1">Play the community quiz and score 100% to win a $5 coupon.</p>
          <Link to="/games" data-testid="btn-goto-games" className="samaaj-btn-primary text-sm mt-4 inline-flex items-center gap-1"><Trophy size={14} weight="fill" /> Play &amp; Earn</Link>
        </div>
      ) : (
        <div className="mt-8 space-y-3" data-testid="coupons-list">
          {active.map((c) => (
            <div key={c.id} className="samaaj-card p-5 flex items-center gap-4" data-testid={`coupon-${c.id}`}>
              <div className="w-14 h-14 rounded-2xl bg-[#D95A41] text-[#F9F6F0] flex items-center justify-center shrink-0"><Ticket size={26} weight="fill" /></div>
              <div className="flex-1 min-w-0">
                <div className="font-mono text-lg tracking-widest text-[#1C1B1A] break-all">{c.code}</div>
                <div className="text-xs text-[#5C564D] mt-0.5 flex items-center gap-2 flex-wrap">
                  <span className="samaaj-chip text-[10px]">${c.amount_usd}</span>
                  <span>{c.reason || "Reward"}</span>
                  {c.expires_at && <span>· expires {new Date(c.expires_at).toLocaleDateString()}</span>}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                <button onClick={() => copy(c.code)} data-testid={`btn-copy-${c.id}`} className="samaaj-btn-outline text-xs flex items-center gap-1">
                  {copied === c.code ? <><CheckCircle size={12} weight="fill" className="text-emerald-600" /> Copied</> : <><Copy size={12} /> Copy</>}
                </button>
                <button onClick={() => shareWA(c)} data-testid={`btn-share-${c.id}`} className="samaaj-btn-outline text-xs flex items-center gap-1">
                  <WhatsappLogo size={12} weight="fill" className="text-emerald-600" /> Share
                </button>
                <Link to="/marketplace" data-testid={`btn-use-${c.id}`} className="samaaj-btn-primary text-xs flex items-center gap-1">
                  <ShoppingBag size={12} /> Use
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {(spent.length > 0 || expired.length > 0) && (
        <details className="mt-10" data-testid="past-coupons">
          <summary className="cursor-pointer text-sm font-semibold text-[#5C564D]">Past coupons ({spent.length + expired.length})</summary>
          <div className="mt-3 space-y-2">
            {[...spent, ...expired].map((c) => (
              <div key={c.id} className="samaaj-card p-3 flex items-center gap-3 opacity-70">
                <Ticket size={16} className="text-[#5C564D]" />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-sm line-through">{c.code}</div>
                  <div className="text-[10px] text-[#5C564D]">
                    ${c.amount_usd} · {c.redeemed ? `used ${c.redeemed_at ? new Date(c.redeemed_at).toLocaleDateString() : ""}` : "expired"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function StatCard({ label, value, sub, accent }) {
  return (
    <div className={`samaaj-card p-4 ${accent ? "bg-[#2C4A3B] text-[#F9F6F0] border-[#2C4A3B]" : ""}`}>
      <div className={`text-[10px] uppercase tracking-widest ${accent ? "text-[#F9F6F0]/70" : "text-[#5C564D]"}`}>{label}</div>
      <div className="font-heading text-3xl mt-1">{value}</div>
      <div className={`text-xs mt-1 ${accent ? "text-[#F9F6F0]/70" : "text-[#5C564D]"}`}>{sub}</div>
    </div>
  );
}
